import { ArrowLeftIcon, SearchIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { Button } from 'components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Switch } from 'components/ui/switch';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useIsMobile } from 'hooks/useIsMobile';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { AnimatePresence, m } from 'motion/react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { foldForSearch } from 'utils/foldForSearch';
import { t } from 'utils/getTranslations';
import { stepMotion } from 'utils/motion';
import {
    CheckBoxGroupFilterForm,
    FilterCheckboxInForm,
    MainFilterForm,
    QuickFilterCheckboxGroupForm,
    QuickFilterDropdownMultiselectForm,
    QuickFilterForm,
    SelectOption,
} from '../ListTableTypes';
import { compositeSummary } from '../compositeFilter';
import { changeCheckBoxGroupFilterState, CheckboxGroup } from '../filter-pieces/inputs/CheckboxGroup';
import FilterChecklist from '../filter-pieces/inputs/FilterChecklist';
import QuickFilterDropdown from '../filter-pieces/inputs/QuickFilterDropdown';
import { MainFilterControl } from '../filter-pieces/MainFilters';
import ConditionSwitch from './ConditionSwitch';
import {
    CONDITION_LABEL_IDS,
    conditionOf,
    conditionsFor,
    FilterField,
    fieldLabel,
    filterChips,
    filterFieldGroups,
    FilterState,
    MultiselectForm,
    selectedCount,
    withCondition,
    withoutField,
    yesNoOptions,
} from './filterChips';

/**
 * An open popover: the field on the right, and the copy of the filter state Apply hands over. `fieldKey` is
 * empty while no field is picked, which is how the popover opens below `sm`.
 */
export type FilterPopoverSession = { fieldKey: string; draft: FilterState };

type Props = {
    session: FilterPopoverSession | null;
    onOpen: () => void;
    onClose: () => void;
    onChange: (update: (session: FilterPopoverSession) => FilterPopoverSession) => void;
    onApply: () => void;
    /** A tooltip after a field group's header, by the group's message key. */
    groupTooltips?: Record<string, string>;
};

/**
 * "+ Filter": every field of the list on the left, the chosen field's condition and value on the right. Below
 * `sm` there is room for one of the two: the list, then the picked field's pane, whose Back, and Escape, return to
 * the list. The two cross-fade there.
 */
export default function AddFilterPopover({ session, onOpen, onClose, onChange, onApply, groupTooltips }: Props) {
    const [query, setQuery] = useState('');
    const searchRef = useRef<HTMLInputElement | null>(null);
    const isMobile = useIsMobile();
    // The field Back left, whose row in the list takes the focus back.
    const [leftField, setLeftField] = useState('');
    const showsPaneOnly = isMobile && !!session?.fieldKey;
    const reduced = useReducedMotion();

    const backToList = () => {
        setLeftField(session?.fieldKey ?? '');
        onChange((current) => ({ ...current, fieldKey: '' }));
    };

    const list = session ? (
        <FieldList
            draft={session.draft}
            selectedKey={session.fieldKey}
            focusKey={leftField}
            alone={isMobile}
            query={query}
            onQueryChange={setQuery}
            searchRef={searchRef}
            onSelect={(fieldKey) => {
                setLeftField('');
                onChange((current) => ({ ...current, fieldKey }));
            }}
            onChange={onChange}
            groupTooltips={groupTooltips}
        />
    ) : null;
    const pane = session ? (
        <FieldPane
            session={session}
            onChange={onChange}
            onApply={onApply}
            onBack={showsPaneOnly ? backToList : undefined}
        />
    ) : null;

    return (
        <Popover
            open={session !== null}
            onOpenChange={(open, details) => {
                if (open) {
                    onOpen();
                } else if (showsPaneOnly && details.reason === 'escape-key') {
                    details.cancel();
                    backToList();
                } else {
                    onClose();
                    setQuery('');
                    setLeftField('');
                }
            }}
        >
            <PopoverTrigger
                data-tour="list-add-filter"
                className="border-border-dashed text-foreground-secondary hover:bg-surface-subtle text-meta data-popup-open:border-primary data-popup-open:bg-primary-wash data-popup-open:text-primary-strong new:select-none h-8 shrink-0 rounded-full border border-dashed px-2.5 font-medium whitespace-nowrap data-popup-open:border-solid"
            >
                {t('filters.add')}
            </PopoverTrigger>
            <PopoverContent
                // Centred on the trigger, so it opens over the list rather than out to the viewport's edge.
                align="center"
                // Stacked with the bar's other dropdowns, above a dialog the list is drawn in.
                positionerClassName="z-(--z-dropdown)"
                initialFocus={searchRef}
                // As tall as the room the positioner measured on its side of the trigger, so it never opens past
                // the viewport's edge. Positioned, so on a phone the pane leaving is laid out in it, over the one
                // arriving.
                className="relative grid h-[min(520px,var(--available-height))] w-[600px] max-w-[calc(100vw-32px)] grid-cols-[236px_minmax(0,1fr)] gap-0 overflow-hidden rounded-[12px] p-0 text-sm max-sm:grid-cols-[minmax(0,1fr)]"
            >
                {session && isMobile ? (
                    <AnimatePresence initial={false} mode="popLayout">
                        <m.div
                            key={showsPaneOnly ? 'pane' : 'list'}
                            className="grid min-h-0 min-w-0"
                            {...stepMotion(reduced)}
                        >
                            {showsPaneOnly ? pane : list}
                        </m.div>
                    </AnimatePresence>
                ) : (
                    <>
                        {list}
                        {pane}
                    </>
                )}
            </PopoverContent>
        </Popover>
    );
}

function FieldList({
    draft,
    selectedKey,
    focusKey,
    alone,
    query,
    onQueryChange,
    searchRef,
    onSelect,
    onChange,
    groupTooltips,
}: {
    draft: FilterState;
    selectedKey: string;
    /** The field whose row takes the focus as the list mounts. */
    focusKey: string;
    /** The list stands without the pane beside it, so without the pane's Apply. */
    alone: boolean;
    query: string;
    onQueryChange: (query: string) => void;
    searchRef: React.RefObject<HTMLInputElement | null>;
    onSelect: (fieldKey: string) => void;
    onChange: Props['onChange'];
    groupTooltips: Props['groupTooltips'];
}) {
    const { setCheckboxes } = draftWriters(draft, onChange);
    const needle = foldForSearch(query);
    const inUse = new Set(filterChips(draft, (value) => value).map((chip) => chip.field.key));
    const groups = filterFieldGroups(draft)
        .map((group) => ({
            ...group,
            fields: group.fields.filter((field) => foldForSearch(fieldLabel(field)).includes(needle)),
        }))
        .filter((group) => group.fields.length > 0);

    return (
        <div className="bg-surface-tile border-neutral-tint flex min-h-0 flex-col gap-0.5 border-e px-2 py-2.5 max-sm:border-e-0">
            <InputGroup className="bg-paper border-border-control mb-1 h-[34px] shrink-0 shadow-none">
                <InputGroupAddon align="inline-start">
                    <SearchIcon className="size-3.5" />
                </InputGroupAddon>
                <InputGroupInput
                    ref={searchRef}
                    placeholder={t('filters.filter-by')}
                    aria-label={t('filters.filter-by')}
                    value={query}
                    onChange={(event) => onQueryChange(event.target.value)}
                />
            </InputGroup>
            {/* One track as wide as the list, so a long name truncates on a phone instead of widening the list. */}
            <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] content-start overflow-y-auto overscroll-contain">
                {groups.map((group) => (
                    <div key={group.title ?? ''} role="group" aria-label={group.title ? t(group.title) : undefined}>
                        {group.title ? (
                            <div className="text-foreground-tertiary text-label flex items-center gap-1 px-2 pt-2 pb-0.5 uppercase">
                                {t(group.title)}
                                {groupTooltips?.[group.title] ? (
                                    <InfoTooltip
                                        text={groupTooltips[group.title]}
                                        label={t(group.title)}
                                        className="h-4"
                                    />
                                ) : null}
                            </div>
                        ) : null}
                        {group.fields.map((field) => {
                            const yesNo = yesNoOptions(field);
                            const disabled =
                                field.kind === 'quick' &&
                                'getShouldDisable' in field.filter &&
                                field.filter.getShouldDisable?.({ mainFilters: draft.mainFilterState }) === true;
                            return (
                                // `relative` keeps the switch's hidden input in its row as the list scrolls.
                                <div
                                    key={field.key}
                                    className={cn(
                                        'text-foreground/70 relative flex items-center rounded-sm text-sm font-medium',
                                        !disabled && 'hover:bg-neutral-tint',
                                        field.key === selectedKey &&
                                            'bg-primary-tint text-foreground hover:bg-primary-tint'
                                    )}
                                >
                                    <button
                                        type="button"
                                        aria-current={field.key === selectedKey || undefined}
                                        autoFocus={field.key === focusKey}
                                        disabled={disabled}
                                        onClick={() => onSelect(field.key)}
                                        className="flex min-h-[30px] min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm px-2 text-start disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <span className="min-w-0 flex-1 truncate">{fieldLabel(field)}</span>
                                        {inUse.has(field.key) ? (
                                            <span className="text-foreground-tertiary text-caption shrink-0 font-normal">
                                                {t('filters.in-use')}
                                            </span>
                                        ) : null}
                                    </button>
                                    {/* A yes / no field answers Yes from the list: on is Yes, off clears the field;
                                        No stays in the pane. Beside the row's button, as a control cannot sit in one;
                                        its click area is trimmed to that button's padding, so it takes no click
                                        meant for the label. Where the list stands alone the answer opens the
                                        field's pane, which is where it is applied from. */}
                                    {yesNo ? (
                                        <Switch
                                            size="sm"
                                            aria-label={fieldLabel(field)}
                                            className="me-2 cursor-pointer after:-inset-x-2"
                                            checked={yesNo.yes.isSelected}
                                            disabled={disabled}
                                            onCheckedChange={(checked) => {
                                                setCheckboxes(
                                                    field,
                                                    (option) => checked && option.key === yesNo.yes.key
                                                );
                                                if (alone) onSelect(field.key);
                                            }}
                                        />
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

const fieldOf = ({ mainFilterState, quickFilterState }: FilterState, key: string): FilterField | undefined => {
    if (mainFilterState[key]) return { kind: 'main', key, filter: mainFilterState[key] };
    return quickFilterState[key] ? { kind: 'quick', key, filter: quickFilterState[key] } : undefined;
};

/**
 * What writes the popover's copy of the filter state: the pane's controls and the list's yes / no switches share
 * them, so a filter's own `onChange` runs whichever of the two moved it.
 */
function draftWriters(draft: FilterState, onChange: Props['onChange']) {
    const setMain = (mainFilterState: MainFilterForm) =>
        onChange((current) => ({ ...current, draft: { ...current.draft, mainFilterState: { ...mainFilterState } } }));
    const setQuick = (quickFilterState: QuickFilterForm) =>
        onChange((current) => ({ ...current, draft: { ...current.draft, quickFilterState: { ...quickFilterState } } }));

    // What the drawer's controls call to move a quick checkbox, here applied to the copy.
    const toggleQuickCheckbox = (value: boolean, groupSelector: string, checkboxKey: string) =>
        onChange((current) => {
            const group = current.draft.quickFilterState[groupSelector];
            if (!group || ('type' in group && group.type === 'dropdown-multiselect')) return current;
            const next = { ...group } as QuickFilterCheckboxGroupForm;
            changeCheckBoxGroupFilterState(checkboxKey, value, next);
            return {
                ...current,
                draft: {
                    ...current.draft,
                    quickFilterState: { ...current.draft.quickFilterState, [groupSelector]: next },
                },
            };
        });

    // Sets a checkbox group's options and runs its `onChange` for each one that moved, as the drawer
    // and the quick-filter row do for the checkbox clicked.
    const setCheckboxes = (field: FilterField, isSelected: (option: FilterCheckboxInForm) => boolean) => {
        const { key } = field;
        const group = field.filter as CheckBoxGroupFilterForm | QuickFilterCheckboxGroupForm;
        const moved = group.filters.filter((option) => option.isSelected !== isSelected(option));
        const next = {
            ...group,
            filters: group.filters.map((option) => ({ ...option, isSelected: isSelected(option) })),
        };
        if (field.kind === 'main') {
            const mainFilter = next as CheckBoxGroupFilterForm;
            setMain({ ...draft.mainFilterState, [key]: mainFilter });
            moved.forEach((option) =>
                mainFilter.onChange?.(
                    { label: option.argument, value: isSelected(option) } as unknown as SelectOption,
                    key,
                    draft.quickFilterState,
                    toggleQuickCheckbox
                )
            );
        } else {
            const quickFilter = next as QuickFilterCheckboxGroupForm;
            setQuick({ ...draft.quickFilterState, [key]: quickFilter });
            moved.forEach((option) =>
                quickFilter.onChange?.({
                    isSelected: isSelected(option),
                    value: option.value,
                    groupSelector: key,
                    mainFilterState: draft.mainFilterState,
                    handleMainFilterStateChange: setMain,
                })
            );
        }
    };

    return { setMain, setQuick, toggleQuickCheckbox, setCheckboxes };
}

function FieldPane({
    session,
    onChange,
    onApply,
    onBack,
}: {
    session: FilterPopoverSession;
    onChange: Props['onChange'];
    onApply: () => void;
    /** Given where the pane stands alone: the way back to the list of fields. */
    onBack?: () => void;
}) {
    // Remounts the value control on Clear: several of the drawer's controls hold their own copy of the value.
    const [clearCount, setClearCount] = useState(0);
    const dateFormat = useEffectiveDateFormat();
    const { draft, fieldKey: key } = session;
    const field = fieldOf(draft, key);
    if (!field) return null;

    const { setMain, setQuick, toggleQuickCheckbox, setCheckboxes } = draftWriters(draft, onChange);

    const label = fieldLabel(field);
    const yesNo = yesNoOptions(field);
    const answer = yesNo ? (yesNo.yes.isSelected ? 'yes' : yesNo.no?.isSelected ? 'no' : '') : '';
    const yesNoCopy = yesNo && 'yesNoCopy' in field.filter ? field.filter.yesNoCopy : undefined;
    const named = 'namedAnswers' in field.filter && field.filter.namedAnswers;
    const ownTooltip = 'tooltip' in field.filter ? field.filter.tooltip : undefined;
    const yesNoTooltip = yesNo ? (yesNoCopy?.help ?? ownTooltip ?? yesNo.yes.tooltip) : undefined;
    // The values of whichever tooltip the help shows: the group's own, or its Yes checkbox's.
    const yesNoTooltipValues =
        yesNoCopy?.help || !yesNo
            ? undefined
            : ownTooltip
              ? 'tooltipValues' in field.filter
                  ? field.filter.tooltipValues
                  : undefined
              : yesNo.yes.tooltipValues;
    const count = yesNo ? undefined : selectedCount(field);
    const terms =
        field.kind === 'main' && field.filter.type === 'text' && Array.isArray(field.filter.value)
            ? field.filter.value.length
            : undefined;
    const controlKey = `${key}:${clearCount}`;
    const condition = conditionOf(field.filter);
    const isEmptiness = condition === 'is-empty' || condition === 'is-not-empty';
    // Where the field words its emptiness, the pane says what it shows and the footer names the condition.
    const emptyCopy = isEmptiness && 'emptyCopy' in field.filter ? field.filter.emptyCopy : undefined;

    const renderControl = () => {
        if (yesNo) {
            const card = (value: 'yes' | 'no', title: string) => {
                const description = yesNoCopy?.[value];
                return (
                    <label
                        className={cn(
                            'border-border-menu flex min-h-11 cursor-pointer items-center gap-2.5 rounded-md border-[1.5px] px-3 py-2.5 font-semibold',
                            description && 'min-h-14 items-start',
                            answer === value && 'border-primary bg-primary-wash'
                        )}
                    >
                        <RadioGroupItem value={value} className={cn(description && 'mt-0.5')} />
                        {description ? (
                            <span className="flex flex-col gap-0.5">
                                <span>{title}</span>
                                <span className="text-foreground-tertiary text-meta font-normal">{t(description)}</span>
                            </span>
                        ) : (
                            title
                        )}
                    </label>
                );
            };
            return (
                <RadioGroup
                    value={answer}
                    onValueChange={(value) =>
                        setCheckboxes(field, (option) =>
                            value === 'yes' ? option.key === yesNo.yes.key : option.key === yesNo.no?.key
                        )
                    }
                    className="gap-1.5"
                >
                    {card('yes', named ? yesNo.yes.label : t('filters.yes'))}
                    {yesNo.no ? card('no', named ? yesNo.no.label : t('filters.no')) : null}
                </RadioGroup>
            );
        }
        if (field.kind === 'quick') {
            const quickFilter = field.filter;
            if ('type' in quickFilter && quickFilter.type === 'dropdown-multiselect') {
                return (
                    <QuickFilterDropdown
                        key={controlKey}
                        filter={quickFilter}
                        onChange={(selected) =>
                            setQuick({
                                ...draft.quickFilterState,
                                [key]: { ...(quickFilter as QuickFilterDropdownMultiselectForm), selected },
                            })
                        }
                    />
                );
            }
            const group = quickFilter as QuickFilterCheckboxGroupForm;
            return (
                <CheckboxGroup
                    key={controlKey}
                    filter={{ ...group, type: 'checkboxgroup' } as CheckBoxGroupFilterForm}
                    direction="column"
                    quickFilters={draft.quickFilterState}
                    mainFilters={draft.mainFilterState}
                    onChange={(checked, _argument, _groupSelector, checkboxKey) =>
                        setCheckboxes(field, (option) =>
                            option.key === checkboxKey
                                ? checked
                                : checked && group.multiple !== true
                                  ? false
                                  : option.isSelected
                        )
                    }
                />
            );
        }
        if (isEmptiness) {
            return emptyCopy ? (
                <p className="text-foreground-tertiary text-meta">
                    {t(condition === 'is-empty' ? emptyCopy.empty : emptyCopy.notEmpty, { label })}
                </p>
            ) : null;
        }
        const mainFilter = field.filter;
        // The checklist has no exclude checkbox or null option: the condition switch stands in for both.
        if (mainFilter.type === 'multiselect' || mainFilter.type === 'dynamicmultiselect') {
            return (
                <FilterChecklist
                    key={controlKey}
                    filter={mainFilter}
                    label={label}
                    quickFilters={draft.quickFilterState}
                    mainFiltersInForm={Object.values(draft.mainFilterState)}
                    onChange={(selected) => {
                        setMain({ ...draft.mainFilterState, [key]: { ...mainFilter, selected } });
                        mainFilter.onChange?.(selected, key, draft.quickFilterState, toggleQuickCheckbox);
                    }}
                />
            );
        }
        return (
            <MainFilterControl
                key={controlKey}
                filter={mainFilter}
                filterState={draft.mainFilterState}
                handleChange={setMain}
                quickFilters={draft.quickFilterState}
                handleQuickFilterChange={toggleQuickCheckbox}
                mainFiltersInForm={Object.values(draft.mainFilterState)}
            />
        );
    };

    const conditions = conditionsFor(field.filter);
    const controlTooltips = 'controlTooltips' in field.filter ? field.filter.controlTooltips : undefined;
    const conditionTooltips = [
        conditions.includes('is-not') && controlTooltips?.isNot,
        conditions.some((condition) => condition === 'is-empty' || condition === 'is-not-empty') &&
            controlTooltips?.empty,
    ].filter((id): id is string => !!id);

    return (
        <div className="flex min-h-0 min-w-0 flex-col">
            <div className="border-divider-soft flex min-h-14 shrink-0 flex-wrap items-center gap-2.5 border-b px-4 pt-3.5 pb-2.5">
                {onBack ? (
                    // Focused as the pane takes the list's place, which had the focus.
                    <Button
                        autoFocus
                        variant="ghost"
                        size="icon-sm"
                        className="text-grey-600 -ms-2"
                        aria-label={t('back')}
                        onClick={onBack}
                    >
                        <ArrowLeftIcon className="size-4 rtl:-scale-x-100" />
                    </Button>
                ) : null}
                {yesNoTooltip ? (
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="font-semibold">{label}</span>
                        <span className="text-foreground-tertiary text-meta">
                            <FormattedMessage id={yesNoTooltip} values={yesNoTooltipValues} />
                        </span>
                    </div>
                ) : (
                    // Sized by the whole name: a condition switch with no room beside it wraps under the name instead
                    // of squeezing it.
                    <span className="flex min-w-0 flex-auto items-center gap-1">
                        <span className="min-w-0 truncate font-semibold">{label}</span>
                        {'tooltip' in field.filter && field.filter.tooltip ? (
                            <InfoTooltip
                                text={field.filter.tooltip}
                                values={'tooltipValues' in field.filter ? field.filter.tooltipValues : undefined}
                                label={label}
                                className="h-4"
                            />
                        ) : null}
                    </span>
                )}
                {conditions.length > 1 ? (
                    <span className="flex items-center gap-1">
                        <ConditionSwitch
                            items={conditions.map((condition) => ({
                                value: condition,
                                label: t(CONDITION_LABEL_IDS[condition]),
                            }))}
                            value={conditionOf(field.filter)}
                            onChange={(condition) =>
                                setMain({
                                    ...draft.mainFilterState,
                                    [key]: withCondition(field.filter as MultiselectForm, condition),
                                })
                            }
                        />
                        {conditionTooltips.length ? (
                            <InfoTooltip text={conditionTooltips} label={t('filters.condition')} className="h-4" />
                        ) : null}
                    </span>
                ) : null}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-4 py-3">
                {renderControl()}
            </div>
            <div className="border-neutral-tint flex h-14 shrink-0 items-center gap-2 border-t ps-4 pe-3">
                <span className="text-foreground-tertiary flex-1 text-xs">
                    {emptyCopy
                        ? t(CONDITION_LABEL_IDS[condition])
                        : count !== undefined
                          ? t('filters.n-selected', { count: String(count) })
                          : terms !== undefined
                            ? t('filters.n-terms', { count: String(terms) })
                            : answer
                              ? named
                                  ? (answer === 'yes' ? yesNo?.yes : yesNo?.no)?.label
                                  : t(answer === 'yes' ? 'filters.yes' : 'filters.no')
                              : field.kind === 'main' && field.filter.type === 'composite'
                                ? compositeSummary(field.filter, (value) => format(new Date(value), dateFormat)).join(
                                      ' · '
                                  )
                                : null}
                </span>
                <Button
                    variant="ghost"
                    className="text-foreground-secondary text-meta h-[34px] px-3 font-medium"
                    onClick={() => {
                        onChange((current) => ({ ...current, draft: withoutField(current.draft, field) }));
                        setClearCount((value) => value + 1);
                    }}
                >
                    {t('clear')}
                </Button>
                <Button className="text-meta h-[34px] px-3.5 font-semibold" onClick={onApply}>
                    {t('apply')}
                </Button>
            </div>
        </div>
    );
}
